/**
 * The two decisions behind the template editor, kept out of the component so
 * they can be held to by a test.
 *
 * Both exist because of the same thing: `email_templates.key` is the primary
 * key **and** it is load-bearing. `isGuidanceTemplate` is `key.startsWith('_')`,
 * and a guidance template is filtered out of the composer entirely. So a key is
 * not a name — it is machinery, and Renee must never have to type one.
 */

/** Reserved for guidance notes, which are never offered as something to send. */
const GUIDANCE_PREFIX = '_'

/**
 * Turn what she typed into a key that cannot collide and cannot hide itself.
 *
 * Three things go wrong if she types keys directly, and all three are silent or
 * baffling rather than loud:
 *
 *  1. **A duplicate is a primary-key violation** — a database error in front of
 *     somebody who wrote a name.
 *  2. **A leading underscore makes it guidance**, so the template saves
 *     perfectly and then never appears in the composer, with nothing on any
 *     screen explaining why.
 *  3. **An empty slug** — a name of "!!!" — would key on nothing at all.
 *
 * So: strip to lowercase words, refuse a leading underscore rather than
 * escaping it, fall back to a stable stem, and suffix on collision. The caller
 * passes the keys that already exist; this never asks the database, so it stays
 * pure and the component decides when to re-check.
 */
export function templateKey(name: string, existing: readonly string[]): string {
  const taken = new Set(existing)

  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      // Leading separators go entirely. Trimming to `_` would keep the very
      // thing that makes a template invisible.
      .replace(/^[-_]+/, '')
      .replace(/-+$/, '')
      .slice(0, 40)
      .replace(/-+$/, '') || 'template'

  if (!taken.has(base)) return base
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base}-${n}`
    if (!taken.has(candidate)) return candidate
  }
  // Unreachable in practice — a thousand templates called the same thing.
  // Throwing beats returning a key that overwrites one of them.
  throw new Error(`Could not find a free key for "${name}"`)
}

/** Is this key one of the guidance notes, which are never sendable? */
export const isGuidanceKey = (key: string) => key.startsWith(GUIDANCE_PREFIX)

/**
 * Put text where the cursor is, not at the end.
 *
 * The composer's merge buttons appended to the end of the body, which is
 * tolerable while writing something new and wrong when editing a saved template
 * — `{{first_name}}` belongs in the middle of a sentence, and having to cut and
 * paste it there defeats the button.
 *
 * Returns the new caret position as well, because leaving the cursor where it
 * was puts the next click's insertion *before* this one.
 */
export function insertAtCursor(
  text: string,
  start: number | null,
  end: number | null,
  insert: string,
): { text: string; caret: number } {
  /**
   * `null` means "there is no cursor" and appends. **0 does not.**
   *
   * This was `start ?? text.length`, which reads correctly and is wrong: a
   * field that has never been focused reports `selectionStart` of **0**, not
   * null, so the fallback never ran and every merge field landed at character
   * zero. `{{first_name}}Hi there,` — worse than the appending it replaced,
   * and flatly contradicting the button's own label.
   *
   * The caller decides whether a cursor exists (by asking whether the element
   * is actually focused) and passes `null` when it does not. Nothing here can
   * tell the difference between "the caret is at the start" and "there is no
   * caret", so nothing here should try.
   */
  const len = text.length
  const hasCursor = typeof start === 'number' && Number.isFinite(start) && start >= 0
  const from = hasCursor ? Math.min(start, len) : len
  const to =
    typeof end === 'number' && Number.isFinite(end) && end >= from ? Math.min(end, len) : from
  return {
    text: text.slice(0, from) + insert + text.slice(to),
    caret: from + insert.length,
  }
}
